import { Box, Card, CardContent, Typography, IconButton, Tooltip } from '@mui/material';
import { Share } from '@mui/icons-material';
import ImageBox from 'components/basic/ImageBox';
import { format } from 'date-fns';
import React, { memo, useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import Inscricao from 'entity/Inscricao';

const formatDate = (dateStr) => format(new Date(dateStr), "dd/MM, HH:mm");

const parseDescription = (text) => {
    // Regex global para links HTTP/HTTPS
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, idx) => {
        if (urlRegex.test(part)) {
            return (
                <Link
                    key={idx}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ wordBreak: 'break-word' }}
                >
                    {part}
                </Link>
            );
        }
        return <span key={idx}>{part}</span>;
    });
};


const CardRodada = memo(({ rodada, children, showNumInscricoes = false, onCompartilhar }) => {
    const [inscricoesTotais, setInscricoesTotais] = useState(0);

    useEffect(() => {
        const buscarInscricoes = async () => {
            try {
                // Busca todas as inscrições (confirmadas + pendentes)
                let queryInscricoes = await Inscricao.where("rodada.uid", "==", rodada.uid);
                queryInscricoes = queryInscricoes.where("status", "in", ["INSCRITO", "PENDENTE/INSCRICAO"]);
                const snapshot = await queryInscricoes.get();
                setInscricoesTotais(snapshot.size);

            } catch (error) {
                console.error("Erro ao buscar inscrições:", error);
                setInscricoesTotais(0);
            }
        };

        if (rodada.uid) {
            buscarInscricoes();
        }
    }, [rodada.uid]);

    return (<Card
        sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: { xs: 420, md: 480 },
            maxHeight: { xs: 'none', md: 'none' },
            borderRadius: 3,
            boxShadow: 4,
            transition: 'box-shadow 0.3s ease',
            overflow: 'hidden',
            '&:hover': {
                boxShadow: 6,
            },
        }}
        role="region"
        aria-labelledby={`rodada-title-${rodada.uid}`}
    >
        <CardContent sx={{
            position: 'relative',
            zIndex: 1,
            p: { xs: 1.5, md: 2 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'column', lg: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: { xs: 'flex-start', md: 'flex-start' },
                flex: 1,
            }}>
                {rodada.capa && (
                    <Box sx={{
                        width: { xs: '100%', md: 350 },
                        height: { xs: 180, md: 350 },
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        flexShrink: 0,
                    }}>
                        <ImageBox
                            src={rodada.capa.base64}
                            alt={`Capa da ${rodada.nome}`}
                            maxWidth={350}
                            maxHeight={350}
                            sx={{
                                width: '100%',
                                height: '100%',
                                '& img': {
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }
                            }}
                        />
                    </Box>
                )}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 'auto', md: 200 },
                    minWidth: { xs: 'auto', md: 200 }
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1
                    }}>
                        <Typography
                            id={`rodada-title-${rodada.uid}`}
                            variant="h6"
                            fontWeight={600}
                            sx={{
                                textTransform: 'uppercase',
                                flex: 1,
                                mr: 1,
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                lineHeight: { xs: 1.3, md: 1.6 },
                                wordBreak: 'break-word',
                                hyphens: 'auto'
                            }}
                        >
                            {rodada.nome}
                        </Typography>{onCompartilhar && (
                            <Tooltip title="Compartilhar rodada" arrow>
                                <IconButton
                                    onClick={() => onCompartilhar(rodada)}
                                    aria-label={`Compartilhar rodada ${rodada.nome}`}
                                    sx={{
                                        color: 'success.main',
                                        '&:hover': {
                                            backgroundColor: 'success.light',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    <Share />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Typography
                        color="text.secondary"
                        gutterBottom
                        sx={{
                            textTransform: 'uppercase',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            wordBreak: 'break-word'
                        }}
                    >
                        📍 {rodada.local}
                    </Typography>
                    <Typography
                        color="text.secondary"
                        gutterBottom
                        sx={{
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                    >
                        🗓️ {`${formatDate(rodada.dataInicio)} até ${formatDate(rodada.dataFinal)}`}
                    </Typography>
                    {showNumInscricoes && inscricoesTotais > 0 && (
                        <Typography
                            color="text.secondary"
                            sx={{
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                            }}
                        >
                            👥 {`${inscricoesTotais} inscritos`}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1,
                            whiteSpace: 'pre-line',
                            flex: 1,
                            minHeight: { xs: '40px', md: '60px' },
                            maxHeight: { xs: '120px', md: '150px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: { xs: 6, md: 8 },
                            lineHeight: 1.5,
                        }}
                    >
                        {parseDescription(rodada.descricao)}
                    </Typography>
                </Box>
            </Box>                <Box sx={{
                pt: { xs: 1.5, md: 2 },
                display: 'flex',
                gap: { xs: 1, md: 2 },
                mt: 'auto',
                justifyContent: 'stretch',
                flexDirection: { xs: 'column', sm: 'row' }
            }}>
                {children}
            </Box>
        </CardContent>
    </Card >
    );
});
export default CardRodada